import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "./api/client";
import BrowseUserIdentityLink from "./components/BrowseUserIdentityLink";
import SettingsSavedNotice from "./components/SettingsSavedNotice";
import UserIdentityLabel from "./components/UserIdentityLabel";
import { useCurrentUser } from "./currentUserContext";
import { buildBrowseUserPath } from "./utils/browseUserPath";

const MOBILE_MESSAGES_BREAKPOINT = 780;

function formatTimestamp(value) {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value);

  if (Number.isNaN(timestamp.getTime())) {
    return null;
  }

  return timestamp.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

function truncatePreview(value, maxLength = 88) {
  const text = String(value || "").trim();

  if (!text) {
    return "No messages yet. Start the conversation here.";
  }

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 1)}...`;
}

function getConversationPreview(conversation) {
  const latestMessageBody = String(conversation?.latestMessage?.body || "").trim();

  if (latestMessageBody) {
    return truncatePreview(latestMessageBody);
  }

  return "No messages in this chat";
}


export default function MyMessagesView() {
  const navigate = useNavigate();
  const { selectedUserId, selectedUser } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeCounterpartyUserId, setActiveCounterpartyUserId] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [draftMessage, setDraftMessage] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [isMobileLayout, setIsMobileLayout] = useState(() => window.innerWidth <= MOBILE_MESSAGES_BREAKPOINT);
  const [isMobileConversationOpen, setIsMobileConversationOpen] = useState(false);
  const messageListRef = useRef(null);
  const activeCounterpartyUserIdRef = useRef(null);

  useEffect(() => {
    activeCounterpartyUserIdRef.current = activeCounterpartyUserId;
  }, [activeCounterpartyUserId]);

  const requestedCounterpartyUserId = Number.parseInt(searchParams.get("userId") || "", 10);

  const loadConversations = useCallback(async (preferredCounterpartyUserId = null) => {
    try {
      setLoadingList(true);
      const response = await apiFetch("/api/messages", { userId: selectedUserId });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not load messages");
      }

      const nextConversations = Array.isArray(data.conversations) ? data.conversations : [];
      const preferredId = Number.isInteger(preferredCounterpartyUserId) && preferredCounterpartyUserId > 0
        ? preferredCounterpartyUserId
        : activeCounterpartyUserIdRef.current;
      const hasPreferredConversation = nextConversations.some((conversation) => Number(conversation.counterparty?.id) === Number(preferredId));
      const resolvedActiveCounterpartyUserId = hasPreferredConversation
        ? Number(preferredId)
        : Number(nextConversations[0]?.counterparty?.id) || null;

      setConversations(nextConversations);
      setActiveCounterpartyUserId(resolvedActiveCounterpartyUserId);
      setError(null);
    } catch (err) {
      console.error("Messages list load error:", err);
      setConversations([]);
      setActiveCounterpartyUserId(null);
      setActiveConversation(null);
      setError(err.message || "Could not load messages");
    } finally {
      setLoadingList(false);
    }
  }, [selectedUserId]);

  const loadActiveConversation = useCallback(async () => {
    if (!Number.isInteger(activeCounterpartyUserId) || activeCounterpartyUserId < 1) {
      setActiveConversation(null);
      return;
    }

    try {
      setLoadingConversation(true);
      const response = await apiFetch(`/api/messages/conversations/${activeCounterpartyUserId}`, { userId: selectedUserId });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not load conversation");
      }

      setActiveConversation(data.conversation || null);
      setConversations((currentValue) => currentValue.map((conversation) => {
        if (Number(conversation.counterparty?.id) !== Number(activeCounterpartyUserId)) {
          return conversation;
        }

        return {
          ...conversation,
          ...(data.conversation || {}),
          unreadCount: 0
        };
      }));
      setError(null);
    } catch (err) {
      console.error("Conversation load error:", err);
      setActiveConversation(null);
      setError(err.message || "Could not load conversation");
    } finally {
      setLoadingConversation(false);
    }
  }, [activeCounterpartyUserId, selectedUserId]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_MESSAGES_BREAKPOINT}px)`);

    const handleMediaQueryChange = (event) => {
      setIsMobileLayout(event.matches);
    };

    setIsMobileLayout(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaQueryChange);
      return () => mediaQuery.removeEventListener("change", handleMediaQueryChange);
    }

    mediaQuery.addListener(handleMediaQueryChange);
    return () => mediaQuery.removeListener(handleMediaQueryChange);
  }, []);

  useEffect(() => {
    if (!isMobileLayout) {
      setIsMobileConversationOpen(false);
      return;
    }

    if (Number.isInteger(requestedCounterpartyUserId) && requestedCounterpartyUserId > 0) {
      setIsMobileConversationOpen(true);
    }
  }, [isMobileLayout, requestedCounterpartyUserId]);

  useEffect(() => {
    if (!selectedUserId) {
      setConversations([]);
      setActiveCounterpartyUserId(null);
      setActiveConversation(null);
      return;
    }

    const preferredCounterpartyUserId = Number.isInteger(requestedCounterpartyUserId) && requestedCounterpartyUserId > 0
      ? requestedCounterpartyUserId
      : null;

    loadConversations(preferredCounterpartyUserId);
  }, [loadConversations, requestedCounterpartyUserId, selectedUserId]);

  useEffect(() => {
    loadActiveConversation();
  }, [loadActiveConversation]);

  useEffect(() => {
    if (
      loadingList &&
      Number.isInteger(requestedCounterpartyUserId) &&
      requestedCounterpartyUserId > 0 &&
      (!Number.isInteger(activeCounterpartyUserId) || activeCounterpartyUserId < 1)
    ) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);

    if (Number.isInteger(activeCounterpartyUserId) && activeCounterpartyUserId > 0) {
      nextParams.set("userId", String(activeCounterpartyUserId));
    } else {
      nextParams.delete("userId");
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeCounterpartyUserId, loadingList, requestedCounterpartyUserId, searchParams, setSearchParams]);

  useEffect(() => {
    const container = messageListRef.current;

    if (!container) {
      return;
    }

    container.scrollTop = container.scrollHeight;
  }, [activeCounterpartyUserId, activeConversation?.messages?.length, loadingConversation]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setNotice(null), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  function openCounterpartyTraders(counterparty) {
    if (!counterparty?.id) {
      return;
    }

    navigate(buildBrowseUserPath(counterparty.id));
  }

  async function handleSendMessage(event) {
    event.preventDefault();

    if (!activeConversationSummary || !draftMessage.trim()) {
      return;
    }

    try {
      setIsSending(true);
      setNotice(null);
      setError(null);

      const response = await apiFetch(`/api/messages/users/${activeConversationSummary.counterparty.id}`, {
        userId: selectedUserId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: draftMessage.trim() })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not send message");
      }

      setDraftMessage("");
      await loadConversations(activeConversationSummary.counterparty.id);
      setNotice(null);
    } catch (err) {
      console.error("Message send error:", err);
      setError(err.message || "Could not send message");
    } finally {
      setIsSending(false);
    }
  }

  const displayedConversations = activeConversation && !conversations.some((conversation) => Number(conversation.counterparty?.id) === Number(activeConversation.counterparty?.id))
    ? [activeConversation, ...conversations]
    : conversations;
  const activeConversationSummary = displayedConversations.find((conversation) => Number(conversation.counterparty?.id) === Number(activeCounterpartyUserId)) || activeConversation || null;
  const showConversationList = !isMobileLayout || !isMobileConversationOpen;
  const showConversationDetail = !isMobileLayout || isMobileConversationOpen;

  const handleSelectConversation = (counterpartyUserId) => {
    setNotice(null);
    setActiveCounterpartyUserId(counterpartyUserId || null);

    if (isMobileLayout && counterpartyUserId) {
      setIsMobileConversationOpen(true);
    }
  };

  const handleBackToConversationList = () => {
    setIsMobileConversationOpen(false);
  };

  return (
    <>
      <div className="account-shell">
        <div className="account-settings-page">
        <div className="account-breadcrumb">
          <Link to="/account">My Account</Link>
          <span>/</span>
          <span>My Messages</span>
        </div>

        <div className="account-settings-panel__header">
          <div>
            <h1 className="account-title">My Messages</h1>
            <p className="account-copy">
              Keep track of your conversations and follow the related trade history with other users.
            </p>
          </div>
        </div>

        {error ? <div className="account-error">{error}</div> : null}

        <div className="app-responsive-split my-messages-split">
            {showConversationList ? (
            <section className="account-settings-panel my-messages-sidebar" style={{ padding: "0", overflow: "hidden" }}>
              <div style={{ padding: "18px 18px 12px", borderBottom: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: "#0f766e" }}>
                  Conversations
                </div>
              </div>

              {loadingList ? (
                <div className="account-loading" style={{ margin: "18px" }}>Loading conversations...</div>
              ) : displayedConversations.length === 0 ? (
                <div style={{ padding: "18px", color: "#475569", lineHeight: 1.6 }}>
                  No trade conversations yet. Chats appear automatically when a trade is proposed, and the same user stays in one shared thread.
                </div>
              ) : displayedConversations.map((conversation) => {
                const isActive = Number(conversation.counterparty?.id) === Number(activeCounterpartyUserId);

                return (
                  <button
                    key={conversation.counterparty?.id}
                    type="button"
                    onClick={() => handleSelectConversation(conversation.counterparty?.id || null)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "16px 18px",
                      border: "0",
                      borderBottom: "1px solid #e2e8f0",
                      background: isActive ? "#ffffff" : "transparent",
                      boxShadow: isActive ? "inset 4px 0 0 #0f766e" : "none",
                      cursor: "pointer",
                      display: "grid",
                      gap: "10px"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                      <UserIdentityLabel
                        username={conversation.counterparty?.username || `User ${conversation.counterparty?.id || ""}`}
                        tier={conversation.counterparty?.collectorTier}
                        role={conversation.counterparty?.role}
                        size="sm"
                        truncateAt={10}
                      />
                      <span style={{ minWidth: "24px", height: "24px", padding: "0 8px", borderRadius: "999px", background: conversation.unreadCount > 0 ? "#f97316" : "#e2e8f0", color: conversation.unreadCount > 0 ? "#ffffff" : "#475569", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 800 }}>
                        {conversation.unreadCount}
                      </span>
                    </div>

                    <div style={{ fontSize: "13px", color: "#475569", lineHeight: 1.5 }}>
                      {getConversationPreview(conversation)}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                      <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
                        {formatTimestamp(conversation.latestMessage?.createdAt || conversation.lastActivityAt) || "No activity yet"}
                      </div>
                    </div>
                  </button>
                );
              })}
            </section>
            ) : null}

            {showConversationDetail ? (
            <section className="account-settings-panel my-messages-detail" style={{ padding: "0", overflow: "hidden" }}>
              {activeConversationSummary ? (
                <div className="my-messages-detail-header">
                  <div style={{ display: "grid", gap: "8px" }}>
                    {isMobileLayout ? (
                      <button
                        type="button"
                        onClick={handleBackToConversationList}
                        className="my-messages-mobile-back"
                      >
                        Back to conversations
                      </button>
                    ) : null}
                    <BrowseUserIdentityLink
                      asButton={true}
                      onClick={() => openCounterpartyTraders(activeConversationSummary.counterparty)}
                      username={activeConversationSummary.counterparty?.username || `User ${activeConversationSummary.counterparty?.id || ""}`}
                      tier={activeConversationSummary.counterparty?.collectorTier}
                      role={activeConversationSummary.counterparty?.role}
                      size="sm"
                      wrap={true}
                      style={{ justifySelf: "start" }}
                    />
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
                    {activeConversationSummary.latestMessage?.createdAt || activeConversationSummary.lastActivityAt
                      ? `Latest activity ${formatTimestamp(activeConversationSummary.latestMessage?.createdAt || activeConversationSummary.lastActivityAt)}`
                      : "No messages yet"}
                  </div>
                </div>
              ) : (
                <div className="my-messages-detail-header" style={{ color: "#64748b", fontWeight: 700 }}>
                  {isMobileLayout ? (
                    <button
                      type="button"
                      onClick={handleBackToConversationList}
                      className="my-messages-mobile-back"
                    >
                      Back to conversations
                    </button>
                  ) : null}
                  <div>Select a user conversation.</div>
                </div>
              )}

              <div ref={messageListRef} className="my-messages-thread" style={{ alignContent: activeConversation?.messages?.length ? "start" : "center" }}>
                {loadingConversation ? (
                  <div className="account-loading" style={{ margin: 0 }}>Loading conversation...</div>
                ) : activeConversation?.messages?.length ? activeConversation.messages.map((message) => {
                  const isOwnMessage = Number(message.senderUserId) === Number(selectedUserId);

                  return (
                    <div
                      key={`${message.id}:${message.dealId || "direct"}`}
                      style={{
                        justifySelf: isOwnMessage ? "end" : "start",
                        maxWidth: "min(560px, 100%)",
                        padding: "14px 16px",
                        borderRadius: isOwnMessage ? "18px 18px 6px 18px" : "18px 18px 18px 6px",
                        background: isOwnMessage ? "#0f766e" : "#ffffff",
                        color: isOwnMessage ? "#ffffff" : "#0f172a",
                        border: isOwnMessage ? "1px solid #0f766e" : "1px solid #dbe4f0",
                        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                        display: "grid",
                        gap: "8px"
                      }}
                    >
                      <div style={{ fontSize: "12px", fontWeight: 800, color: isOwnMessage ? "rgba(255,255,255,0.88)" : "#0f766e" }}>
                        {isOwnMessage ? (selectedUser?.label || "You") : (message.senderUsername || activeConversationSummary?.counterparty?.username || "Counterparty")}
                      </div>
                      <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, wordBreak: "break-word" }}>
                        {message.body}
                      </div>
                      <div style={{ fontSize: "11px", color: isOwnMessage ? "rgba(255,255,255,0.72)" : "#64748b", fontWeight: 600 }}>
                        {formatTimestamp(message.createdAt)}
                      </div>
                    </div>
                  );
                }) : activeConversationSummary ? (
                  <div style={{ padding: "24px", borderRadius: "18px", border: "1px dashed #cbd5e1", background: "rgba(255, 255, 255, 0.7)", color: "#475569", lineHeight: 1.7, maxWidth: "520px" }}>
                    No messages in this chat yet. Send the first note to coordinate payment, shipping, pickup, or anything else related to your trades with this user.
                  </div>
                ) : (
                  <div style={{ padding: "24px", borderRadius: "18px", border: "1px dashed #cbd5e1", background: "rgba(255, 255, 255, 0.7)", color: "#475569", lineHeight: 1.7, maxWidth: "520px" }}>
                    Choose a conversation on the left to open the shared chat.
                  </div>
                )}
              </div>

              <form onSubmit={handleSendMessage} className="my-messages-composer">
                <label style={{ display: "grid", gap: "8px" }}>
                  <span style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>Write message</span>
                  <textarea
                    value={draftMessage}
                    onChange={(event) => setDraftMessage(event.target.value.slice(0, 1200))}
                    rows={4}
                    disabled={!activeConversationSummary || isSending}
                    placeholder={activeConversationSummary ? "Write to the other user about your trades" : "Select a conversation to start writing"}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      borderRadius: "14px",
                      border: "1px solid #cbd5e1",
                      padding: "12px 14px",
                      resize: "vertical",
                      color: "#0f172a",
                      background: !activeConversationSummary ? "#f8fafc" : "#ffffff"
                    }}
                  />
                </label>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ display: "grid", gap: "4px" }}>
                    <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
                      {draftMessage.length} / 1200
                    </span>
                  </div>
                  <button
                    type="submit"
                    disabled={!activeConversationSummary || isSending || !draftMessage.trim()}
                    className="app-button-base"
                    style={{
                      minWidth: "132px",
                      borderRadius: "12px",
                      border: "1px solid #0f766e",
                      background: "#0f766e",
                      color: "#ffffff",
                      cursor: !activeConversationSummary || isSending || !draftMessage.trim() ? "not-allowed" : "pointer",
                      opacity: !activeConversationSummary || isSending || !draftMessage.trim() ? 0.6 : 1
                    }}
                  >
                    {isSending ? "Sending..." : "Send message"}
                  </button>
                </div>
              </form>
            </section>
            ) : null}
          </div>
        </div>
      </div>

      {notice ? <SettingsSavedNotice message={notice} /> : null}
    </>
  );
}